import type { CarrierSummary } from '../carrier/carrier.interface';
import type { RecordedCoordinates } from '../geolocation';
import { HISTORY_CREATED_EVENT_CODE } from '../history/history.interface';
import type { BaseDocument } from '../mongo/base-document.interface';
import type { Delivery, Order, Pickup } from '../order/order.interface';
import type { SiteSummary } from '../site/site.interface';
import type { SyncResource } from '../sync/sync-resource';
import type { UserSummary } from '../user/user.interface';

export const TASK_TYPE = {
  DELIVERY: 'delivery',
  NEW_ATTEMPT: 'new_attempt',
  PICKUP: 'pickup',
  RETURN: 'return',
} as const;
export type TaskType = (typeof TASK_TYPE)[keyof typeof TASK_TYPE];

export const TASK_RELATIONSHIP = {
  CHILD: 'child',
  PARENT: 'parent',
  SIBLING: 'sibling',
} as const;
export type TaskRelationship = (typeof TASK_RELATIONSHIP)[keyof typeof TASK_RELATIONSHIP];

export const TASK_STATUS = {
  ARRIVED_AT_DELIVERY: 'arrived_at_delivery',
  ARRIVED_AT_PICKUP: 'arrived_at_pickup',
  CANCELLED: 'cancelled',
  CARRIER_ACCEPTED: 'carrier_accepted',
  CARRIER_ASSIGNED: 'carrier_assigned',
  CARRIER_REJECTED: 'carrier_rejected',
  COMPLETED: 'completed',
  DRIVER_ACCEPTED: 'driver_accepted',
  DRIVER_ASSIGNED: 'driver_assigned',
  DRIVING_TO_DELIVERY: 'driving_to_delivery',
  DRIVING_TO_PICKUP: 'driving_to_pickup',
  FAILED: 'failed',
  IN_TRANSIT: 'in_transit',
  NEW: 'new',
  PICKUP_COMPLETED: 'pickup_completed',
  WAITING_FOR_PICKUP: 'waiting_for_pickup',
} as const;
export type TaskStatus = (typeof TASK_STATUS)[keyof typeof TASK_STATUS];

// Task statuses representing a terminal driver outcome — successful, failed, or cancelled.
export const TASK_TERMINAL_STATUSES: readonly TaskStatus[] = [
  TASK_STATUS.CANCELLED,
  TASK_STATUS.COMPLETED,
  TASK_STATUS.FAILED,
];

// Page size cap for the task history page/search (shared by backend pagination and mobile list fetch).
export const TASK_HISTORY_PAGE_SIZE = 50;

// Editable window for carrier/driver assignment — through driving_to_pickup.
export const TASK_ASSIGNMENT_EDITABLE_STATUSES: readonly TaskStatus[] = [
  TASK_STATUS.NEW,
  TASK_STATUS.CARRIER_ASSIGNED,
  TASK_STATUS.CARRIER_ACCEPTED,
  TASK_STATUS.CARRIER_REJECTED,
  TASK_STATUS.DRIVER_ASSIGNED,
  TASK_STATUS.DRIVER_ACCEPTED,
  TASK_STATUS.DRIVING_TO_PICKUP,
];

export const TASK_STATUS_REASON = {
  ACCESS_IMPOSSIBLE: 'access_impossible',
  ADDRESS_NOT_FOUND: 'address_not_found',
  AUTO_ASSIGNED_REQUESTED_CARRIER: 'auto_assigned_requested_carrier',
  CLOSED_PREMISES: 'closed_premises',
  CUSTOMER_ABSENT: 'customer_absent',
  DAMAGED_GOODS: 'damaged_goods',
  GOODS_NOT_READY: 'goods_not_ready',
  INCOMPLETE_ORDER: 'incomplete_order',
  ORDER_CANCELLED: 'order_cancelled',
  OTHER: 'other',
  OUTDATED_BY_ORDER_UPDATE: 'outdated_by_order_update',
  POSTPONED: 'postponed',
  REFUSED_BY_CUSTOMER: 'refused_by_customer',
  VEHICLE_BREAKDOWN: 'vehicle_breakdown',
  WRONG_ADDRESS: 'wrong_address',
} as const;
export type TaskStatusReason = (typeof TASK_STATUS_REASON)[keyof typeof TASK_STATUS_REASON];

export const TASK_JOB_TYPE = {
  DELIVERY: 'delivery',
  PICKUP: 'pickup',
} as const;
export type TaskJobType = (typeof TASK_JOB_TYPE)[keyof typeof TASK_JOB_TYPE];

/**
 * Task statuses considered "in progress" per job type, for queue-reorder-pinning purposes: a stop
 * is pinned (cannot be moved) iff at least one of ITS OWN jobs is being executed. The boundary is
 * "arrived at" the job's own leg — a task actively navigating TO a leg (`driving_to_pickup`,
 * `driving_to_delivery`) does NOT pin, since the target of active navigation must stay movable
 * (deliberate product decision). CANCELLED never pins (excluded in `isQueueJobPinned`).
 *
 * - `pickup` job pins its stop when the task is `ARRIVED_AT_PICKUP` or `WAITING_FOR_PICKUP`.
 * - `delivery` job pins its stop when the task is `ARRIVED_AT_DELIVERY`.
 */
export const QUEUE_PINNED_STATUSES_BY_JOB_TYPE: Readonly<Record<TaskJobType, ReadonlySet<TaskStatus>>> = {
  [TASK_JOB_TYPE.DELIVERY]: new Set([TASK_STATUS.ARRIVED_AT_DELIVERY]),
  [TASK_JOB_TYPE.PICKUP]: new Set([TASK_STATUS.ARRIVED_AT_PICKUP, TASK_STATUS.WAITING_FOR_PICKUP]),
};

/**
 * True when a job of type `jobType` on a task with status `status` pins its stop for
 * queue-reorder purposes — see `QUEUE_PINNED_STATUSES_BY_JOB_TYPE` for the semantics.
 */
export function isQueueJobPinned(jobType: TaskJobType, status: TaskStatus): boolean {
  return status !== TASK_STATUS.CANCELLED && QUEUE_PINNED_STATUSES_BY_JOB_TYPE[jobType].has(status);
}

// The driver-facing statuses a job can be postponed from (the backend additionally tolerates the
// reverted target as an idempotent re-tap) — shared source of truth for mobile (action
// availability) and backend (`POSTPONE_TRANSITIONS` in TaskService).
export const POSTPONABLE_STATUSES_BY_JOB_TYPE: Record<TaskJobType, readonly TaskStatus[]> = {
  [TASK_JOB_TYPE.DELIVERY]: [TASK_STATUS.DRIVING_TO_DELIVERY, TASK_STATUS.ARRIVED_AT_DELIVERY],
  [TASK_JOB_TYPE.PICKUP]: [
    TASK_STATUS.DRIVING_TO_PICKUP,
    TASK_STATUS.ARRIVED_AT_PICKUP,
    TASK_STATUS.WAITING_FOR_PICKUP,
  ],
};

// Driver-declarable subset of TASK_STATUS_REASON — picked from it so codes stay single-sourced.
// Excludes the system-only codes OUTDATED_BY_ORDER_UPDATE and AUTO_ASSIGNED_REQUESTED_CARRIER,
// which a driver must never be able to set directly.
export const TASK_FAILURE_REASON = {
  ACCESS_IMPOSSIBLE: TASK_STATUS_REASON.ACCESS_IMPOSSIBLE,
  ADDRESS_NOT_FOUND: TASK_STATUS_REASON.ADDRESS_NOT_FOUND,
  CLOSED_PREMISES: TASK_STATUS_REASON.CLOSED_PREMISES,
  CUSTOMER_ABSENT: TASK_STATUS_REASON.CUSTOMER_ABSENT,
  DAMAGED_GOODS: TASK_STATUS_REASON.DAMAGED_GOODS,
  GOODS_NOT_READY: TASK_STATUS_REASON.GOODS_NOT_READY,
  INCOMPLETE_ORDER: TASK_STATUS_REASON.INCOMPLETE_ORDER,
  OTHER: TASK_STATUS_REASON.OTHER,
  REFUSED_BY_CUSTOMER: TASK_STATUS_REASON.REFUSED_BY_CUSTOMER,
  VEHICLE_BREAKDOWN: TASK_STATUS_REASON.VEHICLE_BREAKDOWN,
  WRONG_ADDRESS: TASK_STATUS_REASON.WRONG_ADDRESS,
} as const;
export type TaskFailureReason = (typeof TASK_FAILURE_REASON)[keyof typeof TASK_FAILURE_REASON];

const BOTH_TASK_JOB_TYPES: readonly TaskJobType[] = [TASK_JOB_TYPE.DELIVERY, TASK_JOB_TYPE.PICKUP];

// Server-side source of truth for which job type(s) each driver-declarable failure reason
// applies to — enforced in StopsQueueService.failStopJobs and consumed by mobile to
// filter the reasons offered per job type.
export const TASK_FAILURE_REASON_JOB_TYPES: Record<TaskFailureReason, readonly TaskJobType[]> = {
  [TASK_FAILURE_REASON.ACCESS_IMPOSSIBLE]: BOTH_TASK_JOB_TYPES,
  [TASK_FAILURE_REASON.ADDRESS_NOT_FOUND]: BOTH_TASK_JOB_TYPES,
  [TASK_FAILURE_REASON.CLOSED_PREMISES]: BOTH_TASK_JOB_TYPES,
  [TASK_FAILURE_REASON.CUSTOMER_ABSENT]: BOTH_TASK_JOB_TYPES,
  [TASK_FAILURE_REASON.DAMAGED_GOODS]: BOTH_TASK_JOB_TYPES,
  [TASK_FAILURE_REASON.GOODS_NOT_READY]: [TASK_JOB_TYPE.PICKUP],
  [TASK_FAILURE_REASON.INCOMPLETE_ORDER]: BOTH_TASK_JOB_TYPES,
  [TASK_FAILURE_REASON.OTHER]: BOTH_TASK_JOB_TYPES,
  [TASK_FAILURE_REASON.REFUSED_BY_CUSTOMER]: [TASK_JOB_TYPE.DELIVERY],
  [TASK_FAILURE_REASON.VEHICLE_BREAKDOWN]: BOTH_TASK_JOB_TYPES,
  [TASK_FAILURE_REASON.WRONG_ADDRESS]: BOTH_TASK_JOB_TYPES,
};

export const TASK_JOB_STATUS = {
  DONE: 'done',
  FAILED: 'failed',
  TO_DO: 'to_do',
} as const;
export type TaskJobStatus = (typeof TASK_JOB_STATUS)[keyof typeof TASK_JOB_STATUS];

export const TASK_PROOF_TYPE = {
  COMMENT: 'comment',
  IMAGES: 'images',
  SIGNATURE: 'signature',
} as const;
export type TaskProofType = (typeof TASK_PROOF_TYPE)[keyof typeof TASK_PROOF_TYPE];

export interface TaskProofCreatedBy {
  firstName: string;
  lastName: string;
  userId: string;
}

interface TaskProofBase {
  // Client-generated dedup id (v4 uuid), stable across every retry of the same driver capture —
  // see `StopSignatureProofInput`/`StopImageProofInput` for the input side, and
  // `TaskService.buildProofIdentity` for how it is preferred over content-derived identity.
  // Absent on comment proofs (stamped server-side from `CompleteStopJobsBody.comment`, no client
  // input) and on proofs captured before this field existed.
  clientId?: string;
  createdAt: Date;
  createdBy: TaskProofCreatedBy;
  id: string;
}

export interface CommentProof extends TaskProofBase {
  comment: string;
  type: typeof TASK_PROOF_TYPE.COMMENT;
}

export interface SignatureProof extends TaskProofBase {
  signature: string;
  type: typeof TASK_PROOF_TYPE.SIGNATURE;
}

/** A single image within an ImagesProof — original + thumbnail URLs. */
export interface TaskProofImage {
  originalUrl: string;
  thumbnailUrl: string;
}

export interface ImagesProof extends TaskProofBase {
  images: TaskProofImage[];
  type: typeof TASK_PROOF_TYPE.IMAGES;
}

/** A single proof captured on a task's pickup or delivery step (comment, signature, or images). */
export type TaskProof = CommentProof | ImagesProof | SignatureProof;

// ─── Proof upload (client -> server input, before server stamping) ─────────

/** A single image reference within a `StopImageProofInput` — container-relative blob paths, never full URLs. */
export interface StopProofImageInput {
  originalPath: string;
  thumbnailPath: string;
}

export interface StopSignatureProofInput {
  // Client-generated dedup id (v4 uuid) minted once at capture time and re-sent unchanged on
  // every retry, independent of `signaturePath` (a fresh server-issued path is requested on
  // every attempt). See `TaskProofBase.clientId`.
  clientId?: string;
  signaturePath: string;
  type: typeof TASK_PROOF_TYPE.SIGNATURE;
}

export interface StopImageProofInput {
  // See `StopSignatureProofInput.clientId`.
  clientId?: string;
  images: StopProofImageInput[];
  type: typeof TASK_PROOF_TYPE.IMAGES;
}

/**
 * A proof submitted by the driver when completing a stop's jobs — container-relative blob
 * paths only (never full URLs). The server stamps `id`/`createdAt`/`createdBy` before
 * persisting as a `TaskProof`. Comments are captured separately via `CompleteStopJobsBody.comment`,
 * stamped as a `CommentProof` and pushed onto `jobs[].proofs` alongside any stamped
 * `StopProofInput` — not a proof-input variant itself.
 */
export type StopProofInput = StopImageProofInput | StopSignatureProofInput;

// ─── Proof upload URLs ───────────────────────────────────────────────────────

export const PROOF_UPLOAD_EXTENSION = {
  JPG: 'jpg',
  PNG: 'png',
} as const;
export type ProofUploadExtension = (typeof PROOF_UPLOAD_EXTENSION)[keyof typeof PROOF_UPLOAD_EXTENSION];

/** Content-Type Azure Blob Storage expects for each uploadable proof extension. */
export const PROOF_UPLOAD_MIME_TYPE: Record<ProofUploadExtension, string> = {
  [PROOF_UPLOAD_EXTENSION.JPG]: 'image/jpeg',
  [PROOF_UPLOAD_EXTENSION.PNG]: 'image/png',
};

/** Container-relative prefix under which every proof blob is issued and stored. */
export const PROOF_BLOB_PATH_PREFIX = 'proofs';

/**
 * V1 cap on photo proofs captured for a single stop completion — single source of truth shared
 * by the mobile driver app (gallery placeholder count, shutter disable) and the backend's
 * `StopProofInputDTO.images` array validator, so the two can never drift independently. Raising
 * this must be paired with raising `MAX_PROOF_UPLOAD_URLS_PER_CALL` below, or a full-cap
 * completion's upload-URL request is rejected outright.
 */
export const MAX_STOP_PROOF_PHOTOS = 4;

/**
 * Max number of upload URLs a single `POST /queue/proofs/upload-urls` call may request. A full
 * stop completion requests 2 extensions per photo (original + thumbnail) plus 1 for an optional
 * signature, so this must stay `>= 2 * MAX_STOP_PROOF_PHOTOS + 1` — see that constant's own doc.
 */
export const MAX_PROOF_UPLOAD_URLS_PER_CALL = 10;

/**
 * Upper bound accepted for a proof blob path at ingress. A server-issued dated path is
 * `proofs/YYYY/MM/DD/{uuid}.{ext}` (57 chars); the margin absorbs a future prefix change.
 */
export const MAX_PROOF_BLOB_PATH_LENGTH = 128;

/**
 * The exact shape of a server-issued proof blob path, anchored and closed: a client can only
 * ever reference back a path this API itself issued. Rejects traversal, absolute URLs, foreign
 * prefixes, and oversized paths at ingress, so a value that the blob-path normalizer would later
 * refuse can never reach the database.
 *
 * The dated `YYYY/MM/DD/` segment (see `buildDatedBlobPath` in `pharos-lib-back-utils`) is
 * OPTIONAL here: `mintProofBlobPath` only ever mints the dated shape going forward, but a driver
 * can be holding a pending-upload URL issued before this rollout (the offline action queue may
 * replay it days later) — ingress must still accept the legacy flat `proofs/{uuid}.{ext}` shape
 * it minted at the time, or that replay would be permanently rejected with no way to recover.
 *
 * The date segments (when present) are NOT re-validated against `originalPath`'s actual mint
 * date — only the shape is checked here; a mismatched-but-well-formed date is harmless since the
 * path is always resolved against the real blob storage location, never reconstructed from these
 * segments.
 */
export const PROOF_BLOB_PATH_REGEX = new RegExp(
  `^${PROOF_BLOB_PATH_PREFIX}/(?:\\d{4}/\\d{2}/\\d{2}/)?[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\\.(?:${Object.values(
    PROOF_UPLOAD_EXTENSION,
  ).join('|')})$`,
);

/** One issued upload SAS URL — `path` is the container-relative blob path to reference later (never a full URL). */
export interface ProofUploadUrl {
  expiresOn: Date;
  path: string;
  url: string;
}

// ─── Units control (pickup/delivery load & unload declarations) ───────────────

export const HANDLING_UNIT_CONTROL_STATUS = {
  MARKED: 'marked',
  // Expected at that leg but neither scanned nor marked at confirm.
  MISSING: 'missing',
  SCANNED: 'scanned',
} as const;
export type HandlingUnitControlStatus =
  (typeof HANDLING_UNIT_CONTROL_STATUS)[keyof typeof HANDLING_UNIT_CONTROL_STATUS];

export const CONTROLLED_UNIT_SOURCE = {
  ADDITIONAL: 'additional',
  EXPECTED: 'expected',
} as const;
export type ControlledUnitSource = (typeof CONTROLLED_UNIT_SOURCE)[keyof typeof CONTROLLED_UNIT_SOURCE];

/** Stamped once per leg, at confirm. */
export interface UnitsControlDeclaration {
  declaredAt: Date;
  declaredBy: TaskProofCreatedBy;
}

/** One handling-unit line tracked across the pickup/delivery load-control lifecycle. */
export interface ControlledUnit {
  // Leg this unit was added at — additional units only.
  addedAt?: TaskJobType;
  // Barcode VALUE only (snapshot at confirm time for an expected line's `HandlingUnit.code.value`,
  // or the raw scanned string for a scanned additional unit) — never the full `Barcode` {type,
  // value} shape, since a scanned additional unit has no known barcode standard.
  code?: string;
  // Absent until delivery confirm, and for units missing at pickup.
  deliveryStatus?: HandlingUnitControlStatus;
  // Absent until pickup confirm, and for delivery-added units.
  pickupStatus?: HandlingUnitControlStatus;
  // Snapshot at confirm time.
  quantity: number;
  source: ControlledUnitSource;
  // Snapshot at confirm time.
  type: string;
  // Order HU line id, or minted for additional units.
  unitId: string;
}

/** Driver load/unload control declarations for a task — one record per unit, recorded at stop completion. */
export interface TaskUnitsControl {
  deliveryDeclaration?: UnitsControlDeclaration;
  pickupDeclaration?: UnitsControlDeclaration;
  units: ControlledUnit[];
}

export interface RelatedTask {
  id: string;
  relationship: TaskRelationship;
}

export interface TaskJobBase {
  id: string;
  proofs?: TaskProof[];
  sortIndex: number;
  status: TaskJobStatus;
}

export interface DeliveryJob extends TaskJobBase {
  type: typeof TASK_JOB_TYPE.DELIVERY;
  data: Delivery;
}

export interface PickupJob extends TaskJobBase {
  type: typeof TASK_JOB_TYPE.PICKUP;
  data: Pickup;
}

export type TaskJob = DeliveryJob | PickupJob;

/** Job shape used when creating a task — `id` is generated by MongoDB on the subdocument. */
export type TaskJobInput = Omit<TaskJob, 'id'>;

export interface Task extends BaseDocument {
  carrier?: CarrierSummary | null;
  // Stamped atomically with the transition to `completed`, `failed`, or `cancelled` — never set
  // for `pickup_completed` (intermediate leg), and never re-stamped on idempotent replays of an
  // already-terminal task. Semantics: "reached a terminal status", not just "driver success".
  completedAt?: Date | null;
  driver?: UserSummary | null;
  jobs: TaskJob[];
  // `referenceNumber` is optional here even though it is required on `Order`: migration 0028
  // deliberately leaves tasks whose order is gone (or whose `referenceNumber` was unresolvable)
  // without it, so some documents will lack it forever.
  order: Pick<Order, 'id' | 'instruction' | 'tags' | 'totalHandlingUnitQuantity'> &
    Partial<Pick<Order, 'referenceNumber'>>;
  relatedTasks?: RelatedTask[];
  shipperId: string;
  site: SiteSummary;
  spaceId: string;
  // Set once when the task first enters an in-progress status; never cleared.
  startedAt?: Date | null;
  status: TaskStatus;
  // Driver position at the most recent driver-declared status transition; null when that action carried no GPS.
  statusCoordinates?: RecordedCoordinates | null;
  statusReason?: string | null;
  statusReasonMessage?: string | null;
  type: TaskType;
  // Driver load/unload control declarations, recorded at stop completion.
  unitsControl?: TaskUnitsControl;
}

/** A completed/failed task hydrated with its full order — the `GET /tasks/completed` response item. */
export interface CompletedTaskItem {
  order: Order;
  task: Task;
}

/** Wire contract for `GET /tasks/:id/history` `HistoryEntry.code` values; `CREATED` reuses the platform's fixed created sentinel. */
export const TASK_HISTORY_EVENT_CODE = {
  CARRIER: 'carrier',
  CREATED: HISTORY_CREATED_EVENT_CODE,
  DRIVER: 'driver',
  STATUS: 'status',
} as const;
export type TaskHistoryEventCode = (typeof TASK_HISTORY_EVENT_CODE)[keyof typeof TASK_HISTORY_EVENT_CODE];

// ─── Sync resource descriptor (declaration-merged with the Task interface) ───

/**
 * Runtime descriptor for Task collection synchronization.
 * Use `@SyncSource(Task)` on a service to publish create/update/delete events.
 */
export const Task: SyncResource<Task> = {
  collection: 'tasks',
  name: 'task',
};
